import React from 'react';
import { Badge, Card, DataList, Flex, Grid, Text } from '@/components/ui';
import { IconDatabase, IconFileImport } from '@tabler/icons-react';
import type { MappingData } from '../../utils/transformMappingData';
import { ImportBasicInfo } from './ImportBasicInfo';

interface ImportStatisticsCardsProps {
  mappingData: MappingData;
}

export const ImportStatisticsCards: React.FC<ImportStatisticsCardsProps> = ({
  mappingData,
}) => {
  return (
    <Grid gap="4" columns="3">
      {/* Data Summary Card */}
      <Card style={{ flex: 1, minWidth: '300px' }}>
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <IconDatabase color="var(--blue-9)" />
            <Text size="3" weight="medium">
              Data Summary
            </Text>
          </Flex>

          <DataList
            ml="5"
            mt="2"
            size="small"
            orientation="horizontal"
            alignValues="end"
            items={[
              {
                label: 'Sites Created:',
                value: (
                  <Badge
                    color={
                      typeof mappingData._apiData?.sites_created === 'number'
                        ? 'info'
                        : 'gray'
                    }
                    variant="soft"
                  >
                    {typeof mappingData._apiData?.sites_created === 'number'
                      ? mappingData._apiData.sites_created
                      : 'N/A'}
                  </Badge>
                ),
              },
              {
                label: 'Subjects Created:',
                value: (
                  <Badge
                    color={
                      typeof mappingData._apiData?.subjects_created === 'number'
                        ? 'success'
                        : 'gray'
                    }
                    variant="soft"
                  >
                    {typeof mappingData._apiData?.subjects_created === 'number'
                      ? mappingData._apiData.subjects_created
                      : 'N/A'}
                  </Badge>
                ),
              },
              {
                label: 'Forms Processed:',
                value: (
                  <Badge color="warning" variant="soft">
                    {typeof mappingData._apiData?.forms_processed === 'number'
                      ? mappingData._apiData.forms_processed
                      : mappingData._calculatedStats?.formsCount || 0}
                  </Badge>
                ),
              },
              {
                label: 'Records Imported:',
                value: (
                  <Badge
                    color={
                      mappingData.records_processed !== undefined ||
                      mappingData.total_records !== undefined
                        ? 'feature'
                        : 'gray'
                    }
                    variant="soft"
                  >
                    {mappingData.records_processed !== undefined ||
                    mappingData.total_records !== undefined
                      ? `${mappingData.records_processed || 0} / ${mappingData.total_records || 0}`
                      : 'Not started'}
                  </Badge>
                ),
              },
              ...(mappingData.success_count !== undefined
                ? [
                    {
                      label: 'Success Count:',
                      value: (
                        <Badge color="success" variant="soft">
                          {mappingData.success_count}
                        </Badge>
                      ),
                    },
                  ]
                : []),
              ...(mappingData.error_count !== undefined &&
              mappingData.error_count > 0
                ? [
                    {
                      label: 'Error Count:',
                      value: (
                        <Badge color="error" variant="soft">
                          {mappingData.error_count}
                        </Badge>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Flex>
      </Card>

      {/* Import Details Card */}
      <Card style={{ flex: 1, minWidth: '300px' }}>
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <IconFileImport color="var(--green-9)" />
            <Text size="3" weight="medium">
              Import Details
            </Text>
          </Flex>

          <DataList
            items={[
              {
                label: 'Files Processed:',
                value: (
                  <Badge color="info" variant="soft">
                    {mappingData._calculatedStats?.filesCount ||
                      mappingData.file_mappings?.length ||
                      0}
                  </Badge>
                ),
              },
              {
                label: 'Forms Mapped:',
                value: (
                  <Badge color="success" variant="soft">
                    {mappingData._calculatedStats?.formsCount ||
                      (Array.isArray(
                        mappingData._apiData?.file_to_form_mappings
                      )
                        ? mappingData._apiData.file_to_form_mappings.length
                        : 0)}
                  </Badge>
                ),
              },
              {
                label: 'Mapping ID:',
                value: (
                  <Badge color="feature" variant="soft">
                    #{mappingData.id}
                  </Badge>
                ),
              },
              {
                label: 'Columns Mapped:',
                value: (
                  <Badge color="warning" variant="soft">
                    {mappingData._calculatedStats?.columnMappingsCount ||
                      (Array.isArray(mappingData._apiData?.column_mappings)
                        ? mappingData._apiData.column_mappings.length
                        : 0)}
                  </Badge>
                ),
              },
              {
                label: 'Success Rate:',
                value: (
                  <Badge
                    color={
                      mappingData.total_records !== undefined &&
                      mappingData.total_records > 0
                        ? mappingData.status === 'completed'
                          ? 'success'
                          : mappingData.status === 'failed'
                            ? 'error'
                            : 'info'
                        : 'gray'
                    }
                    variant="soft"
                  >
                    {mappingData.total_records !== undefined &&
                    mappingData.total_records > 0
                      ? mappingData.success_count !== undefined &&
                        mappingData.total_records > 0
                        ? `${Math.round(
                            (mappingData.success_count /
                              mappingData.total_records) *
                              100
                          )}%`
                        : mappingData.status === 'completed'
                          ? '100%'
                          : '0%'
                      : 'Not available'}
                  </Badge>
                ),
              },
            ]}
            ml="5"
            mt="2"
            size="small"
            orientation="horizontal"
            alignValues="end"
          />
        </Flex>
      </Card>

      {/* Mapping Information */}
      <ImportBasicInfo
        id={mappingData.id}
        createdAt={mappingData.created_at}
        updatedAt={mappingData.updated_at}
        createdBy={mappingData.created_by}
      />
    </Grid>
  );
};
